import React from "react";
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import {
  LocationOn,
  AccessTime,
  Visibility,
  Share as ShareIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { Event } from "../../types";
import {
  getCategoryConfig,
  formatEventTime,
  getEventStatusBadge,
} from "../../utils/eventHelpers";
import { gradients, boxShadows } from "../../utils/theme";

interface EventCardProps {
  event: Event;
  onViewDetails?: (event: Event) => void; // Made optional for backward compatibility
}

/**
 * EventCard Component
 * Displays event information in a card format for grid views
 */
const EventCard: React.FC<EventCardProps> = ({ event, onViewDetails }) => {
  const navigate = useNavigate();
  const categoryConfig = getCategoryConfig(event.category);
  const statusBadge = getEventStatusBadge(
    event.startDate,
    event.endDate,
    event.status
  );

  const eventDate = new Date(event.startDate);
  const monthShort = eventDate
    .toLocaleDateString("en-US", { month: "short" })
    .toUpperCase();
  const day = eventDate.getDate();

  const handleCardClick = () => {
    // Support both callback and navigation for backward compatibility
    if (onViewDetails) {
      onViewDetails(event);
    }
    navigate(`/events/${event.id}`);
  };

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        borderRadius: { xs: 2.5, sm: 3, md: 3.5 },
        cursor: "pointer",
        boxShadow: boxShadows.card,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        "&:hover": {
          transform: "translateY(-6px)",
          boxShadow: boxShadows.cardHover,
          borderColor: "primary.light",
          "& .event-image": {
            transform: "scale(1.05)",
          },
        },
      }}
    >
      {/* Date Badge - Premium Navy */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 10, sm: 12, md: 14 },
          left: { xs: 10, sm: 12, md: 14 },
          background: gradients.navyLux,
          borderRadius: { xs: 2, sm: 2.5 },
          p: { xs: 1, sm: 1.25, md: 1.5 },
          textAlign: "center",
          boxShadow: boxShadows.primary,
          zIndex: 2,
          minWidth: { xs: 52, sm: 60, md: 68 },
          border: "2px solid white",
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            fontWeight: 800,
            color: "white",
            lineHeight: 1,
            fontSize: { xs: "0.75rem", sm: "0.813rem", md: "0.875rem" },
            letterSpacing: "0.5px",
            opacity: 0.9,
          }}
        >
          {monthShort}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 800,
            lineHeight: 1,
            mt: 0.5,
            color: "white",
            fontSize: { xs: "1.25rem", sm: "1.5rem", md: "1.75rem" },
          }}
        >
          {day}
        </Typography>
      </Box>

      {/* Status Badge */}
      {statusBadge.label !== "Upcoming" && (
        <Chip
          label={statusBadge.label}
          size="small"
          color={statusBadge.color as any}
          icon={<span>{statusBadge.icon}</span>}
          sx={{
            position: "absolute",
            top: { xs: 10, sm: 12, md: 14 },
            right: { xs: 10, sm: 12, md: 14 },
            zIndex: 2,
            fontWeight: 700,
            fontSize: { xs: "0.75rem", sm: "0.813rem", md: "0.875rem" },
            height: { xs: 26, sm: 28, md: 30 },
            boxShadow: boxShadows.card,
            border: "2px solid white",
          }}
        />
      )}

      {/* Event Image - Premium Styling */}
      <CardMedia
        component="img"
        className="event-image"
        height="180"
        image={event.thumbnailImage}
        alt={event.title}
        sx={{
          objectFit: "cover",
          transition: "all 0.3s ease",
          height: { xs: 160, sm: 170, md: 180 },
        }}
      />

      <CardContent
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          p: { xs: 1.5, md: 2 },
          pt: { xs: 1, md: 1.25 },
        }}
      >
        {/* Category Chip - Category Color with Emoji Icon */}
        <Box
          sx={{
            mb: { xs: 1, sm: 1.25 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Chip
            label={categoryConfig.label}
            size="small"
            icon={<span style={{ fontSize: 16 }}>{categoryConfig.icon}</span>}
            sx={{
              background: categoryConfig.gradient,
              color: "white",
              fontWeight: 700,
              fontSize: { xs: "0.75rem", sm: "0.813rem", md: "0.875rem" },
              height: { xs: 26, sm: 28, md: 30 },
              boxShadow: boxShadows.card,
              "& .MuiChip-icon": {
                marginLeft: "4px",
              },
            }}
          />
          <Visibility
            sx={{
              fontSize: { xs: 18, sm: 20, md: 22 },
              color: "primary.main",
              opacity: 0.8,
            }}
          />
        </Box>

        {/* Event Title - Compact */}
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            mb: 1,
            fontSize: { xs: "0.938rem", md: "1.063rem" },
            lineHeight: 1.3,
            minHeight: { xs: "2.4rem", md: "2.7rem" },
            flexGrow: 1,
          }}
        >
          {event.title}
        </Typography>

        {/* Venue - Compact */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.75 }}>
          <LocationOn
            sx={{ fontSize: { xs: 16, md: 18 }, color: "primary.main" }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: { xs: "0.813rem", md: "0.875rem" },
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {event.venue}
          </Typography>
        </Box>

        {/* Time - Compact */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            mb: 1,
          }}
        >
          <AccessTime
            sx={{ fontSize: { xs: 16, md: 18 }, color: "text.secondary" }}
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: "0.813rem", md: "0.875rem" } }}
          >
            {formatEventTime(event.startDate)}
            {event.isAllDay && " (All Day)"}
          </Typography>
        </Box>

        {/* View Count (left) and Share Count (right) - Same Line */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
          }}
        >
          {/* View Count - Left */}
          {event.viewCount !== undefined && event.viewCount > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <Visibility
                sx={{ fontSize: { xs: 14, md: 16 }, color: "text.secondary" }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "0.75rem", md: "0.813rem" },
                  fontWeight: 500,
                }}
              >
                {event.viewCount.toLocaleString()} view
                {event.viewCount !== 1 ? "s" : ""}
              </Typography>
            </Box>
          )}

          {/* Share Count - Right */}
          {event.shareCount !== undefined && event.shareCount > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                ml: "auto",
              }}
            >
              <ShareIcon
                sx={{ fontSize: { xs: 14, md: 16 }, color: "text.secondary" }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontSize: { xs: "0.75rem", md: "0.813rem" },
                  fontWeight: 500,
                }}
              >
                {event.shareCount.toLocaleString()} share
                {event.shareCount !== 1 ? "s" : ""}
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default EventCard;
